import { useNavigate, Link, useLocation } from "react-router-dom";
import styles from "./sideBar.module.css";
import membershipStyles from "./addmembershipform.module.css";
import { User, Calendar, FileText, Truck, Power, ExternalLink, X, Menu, ShoppingBag } from "react-feather";
import { authCustomerData } from "../../admin/components/GetAuth";
import dayjs from "dayjs";
import { Alert, Button, Dialog, DialogContent, DialogTitle, Snackbar } from "@mui/material";
import { useState } from "react";
import AddmembershipForm from "./AddmembershipForm";
import Loader from "../../admin/components/Loader";
import { StorageRemoveItem, STORAGE_KEYS } from "../../admin/components/StorageComp";
import { hasFeature } from "../../admin/components/Features";

const getNameInitials = (name) => {
  if (!name) return "";

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const NAV_ITEMS = [
  { to: "/customer/my-bookings", label: "My bookings", icon: FileText },
  { to: "/customer/order-history", label: "Order history", icon: ShoppingBag, feature: "shop" },
  { to: "/customer/add-booking", label: "Add a booking", icon: Calendar },
  { to: "/customer/track-booking", label: "Track a booking", icon: Truck },
  { to: "/customer/referral-histories", label: "Referral history", icon: ExternalLink, feature: "referral" },
  { to: "/customer/profile", label: "My profile", icon: User },
];

function SideBar() {
  const authCustomer = authCustomerData();
  const navigate = useNavigate();
  const location = useLocation();
  const [addMembershipModalShow, setAddMembershipModalShow] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const referralOn = hasFeature("referral");
  const membershipOn = hasFeature("membership");

  const logout = () => {
    StorageRemoveItem(STORAGE_KEYS.AUTH);
    StorageRemoveItem(STORAGE_KEYS.AUTH_CUSTOMER);
    navigate("/");
  };

  const closeDrawer = () => setMobileOpen(false);

  return (
    <>
      <Loader open={loading} setOpen={setLoading} />

      {/* Mobile top bar: only visible under the sidebar breakpoint */}
      <button
        type="button"
        className={styles.MobileTrigger}
        onClick={() => setMobileOpen(true)}
        aria-label="Open account menu"
      >
        <Menu />
        <span>Account menu</span>
      </button>

      {/* Backdrop */}
      <div
        className={`${styles.Overlay} ${mobileOpen ? styles.Show : ""}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      <div className={`${styles.CustoSideNav} ${mobileOpen ? styles.Open : ""}`}>
        <button type="button" className={styles.DrawerClose} onClick={closeDrawer} aria-label="Close menu">
          <X />
        </button>

        <ul>
          <li className={`${styles.ListHead}`}>
            <div className={`${styles.CusAvatar}`}>
              <span>{getNameInitials(authCustomer?.name)}</span>
            </div>
            <div className={styles.ListHeadText}>
              <p>{authCustomer?.name}</p>
              {referralOn && <span>
                Referral points: <strong>£{authCustomer?.referral_point}</strong>
              </span>}
            </div>
          </li>
          {membershipOn && <li className={`${styles.ListHead2}`}>
            {authCustomer?.membership_expired_date && (
              <div className={styles.MembershipNote}>
                Membership expired on{" "}
                <strong>{dayjs(authCustomer?.membership_expired_date).format("MMM D YYYY")}</strong>
              </div>
            )}
            {!authCustomer?.membership_expired_date && authCustomer?.is_membership === 1 && (
              <Button className={`${styles.SideEditBU}`} onClick={() => setAddMembershipModalShow(true)}>
                Subscribe
              </Button>
            )}
          </li>}

          {NAV_ITEMS.filter((item) => !item.feature || hasFeature(item.feature)).map(({ to, label, icon: Icon }) => {
            const active = location?.pathname === to || location?.pathname?.startsWith(to + "/");
            return (
              <li key={to}>
                <Link to={to} onClick={closeDrawer} className={active ? styles.Active : ""}>
                  <Icon /> {label}
                </Link>
              </li>
            );
          })}

          <li>
            <button onClick={logout}>
              <Power /> Logout
            </button>
          </li>
        </ul>
      </div>

      <Dialog
        open={addMembershipModalShow}
        onClose={() => setAddMembershipModalShow(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: membershipStyles.ModalPaper }}
      >
        <DialogTitle className={membershipStyles.ModalHead} component="div">
          <div className={membershipStyles.ModalHeadText}>
            <p className={membershipStyles.ModalEyebrow}>Membership</p>
            <h2 className={membershipStyles.ModalTitle}>Subscribe membership</h2>
          </div>
          <button
            type="button"
            className={membershipStyles.ModalCloseBU}
            onClick={() => setAddMembershipModalShow(false)}
            aria-label="Close"
          >
            <X />
          </button>
        </DialogTitle>
        <DialogContent className={membershipStyles.ModalBody}>
          <AddmembershipForm
            id={authCustomer?.id}
            setAddMembershipModalShow={setAddMembershipModalShow}
            setSnackbarOpen={setSnackbarOpen}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarType={setSnackbarType}
            setLoading={setLoading}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default SideBar;